import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import ContextMenu from 'flow/components/ContextMenu';
import BaseListItem from 'flow/components/ui-components/BaseListItem';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectContact,
    selectContactName,
} from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { twMerge } from 'tailwind-merge';
import MemberContextMenu from './MemberContextMenu';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';

interface Props {
    memberKey: ExtensionKey;
    ticketKey: TicketKey;
}

const MemberItem = ({ memberKey, ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const dispatch = useAppDispatch();
    const [itemIsActive, setItemIsActive] = useState(false);

    const memberName = useAppSelector((state) =>
        selectContactName(state, memberKey),
    );

    const contact = useAppSelector((state) => selectContact(state, memberKey));

    return (
        <BaseListItem
            onClick={() => {
                openModal('contactModal', { extensionKey: memberKey });
                dispatch(
                    segmentTrack(TICKET_STRINGS.OPENED_MEMBERS_CONTACT_MODAL, {
                        component: 'ticket details overview',
                    }),
                );
            }}
            selected={itemIsActive}
        >
            <div className="flex grow justify-between">
                <div className="flex gap-x-4">
                    <ContactAvatar contact={contact} />
                    <div className="flex items-center overflow-hidden text-base/5 text-ellipsis text-gray-800">
                        {memberName || i18next.t('Contact.Unknown')}
                    </div>
                </div>
                <div
                    className={twMerge(
                        'items-center',
                        itemIsActive
                            ? 'flex'
                            : 'sr-only-until-hover-or-focus flex',
                    )}
                >
                    <ContextMenu
                        triggerLabel={getMoreOptionsLabel(i18next, memberName)}
                        setActive={setItemIsActive}
                        preventOverflow={true}
                    >
                        <MemberContextMenu
                            memberKey={memberKey}
                            ticketKey={ticketKey}
                        />
                    </ContextMenu>
                </div>
            </div>
        </BaseListItem>
    );
};

export default React.memo(MemberItem);
